import { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Spin, Empty } from "antd";
import { LockOutlined, UnlockOutlined, UserOutlined, EnvironmentOutlined, ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import SearchBar from "../../components/searchBar";
import StatusTabs from "../../components/statusTabs";
import CustomPagination from "../../components/pagination";
import AddButton from "../../components/addButton";
import { AddLockerRoute } from "../../routes/routepath";
import { useGetAllLockersQuery } from "../../services/lockers";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface LockerRecord {
  _id?:          string;
  lockerNumber?: string | number;
  number?:       string | number;
  name?:         string;
  status?:       string;                       // "purchased" | "unpurchased" | ...
  isPurchased?:  boolean;
  price?:        number | string;
  daysRemaining?: number;
  remainingDays?: number;
  daysLeft?:      number;
  validityDays?:  number;
  expiryDate?:   string;
  endDate?:      string;
  validTill?:    string;
  validUpto?:    string;
  expiresAt?:    string;
  expiry?:       string;
  branchId?:     { name?: string };
  user?:         { name?: string };
  userId?:       { name?: string };
  memberName?:   string;
  purchasedBy?:  { _id?: string; name?: string; email?: string; phoneNumber?: string } | null;
  startDate?:    string | null;
  validUntil?:   string | null;
  membershipId?: string | null;
  purchase?:     Record<string, any>;
  membership?:   Record<string, any>;
}

interface StatusTab {
  key:    string;
  label:  string;
  count?: number;
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

/* ─── Demo data (remove when API sends purchase/validity) ────────────── */
const DEMO = false;
const DEMO_MEMBERS = ["Rahul Sharma", "Priya Verma", "Amit Singh", "Neha Gupta", "Karan Mehta", "Sneha Rao"];
const DEMO_DAYS    = [42, 5, 18, 58, 3, 27, 11, 34];   // includes a couple ≤7 to show "low" state

/** A locker is purchased if flagged, assigned to a user, carries validity data, or
 *  has an explicit "purchased" status. ("unpurchased" → available) */
const isPurchased = (l: LockerRecord): boolean => {
  if (typeof l.isPurchased === "boolean") return l.isPurchased;
  if (l.status) return l.status.toLowerCase() === "purchased";
  if (l.user?.name || l.userId?.name || l.memberName || l.purchasedBy?.name) return true;
  return daysLeft(l) != null;
};

/** Formats a price as ₹ with thousands separators; null if not a valid number. */
const formatPrice = (l: LockerRecord): string | null => {
  const n = typeof l.price === "string" ? Number(l.price) : l.price;
  if (typeof n !== "number" || Number.isNaN(n)) return null;
  return `₹${n.toLocaleString("en-IN")}`;
};

const lockerNo = (l: LockerRecord): string => {
  const n = l.lockerNumber ?? l.number ?? l.name;
  if (n == null || n === "") return "-";
  // strip a leading "L" / "L-" / "L " prefix so only the number shows
  return String(n).replace(/^\s*L\s*-?\s*/i, "").trim() || "-";
};

const userName = (l: LockerRecord): string =>
  l.user?.name || l.userId?.name || l.memberName || l.purchasedBy?.name || "-";

const memberId = (l: LockerRecord): string | undefined => l.purchasedBy?._id;

/** Days left, preferring an explicit numeric field, else derived from an expiry date.
 *  Checks common field names on the record and any nested purchase/membership object. */
const daysLeft = (l: LockerRecord): number | null => {
  const sources: Record<string, any>[] = [l, l.purchase || {}, l.membership || {}];

  // 1) explicit numeric "days remaining" style fields
  const numKeys = ["daysRemaining", "remainingDays", "daysLeft", "validityDays"];
  for (const src of sources) {
    for (const k of numKeys) {
      if (typeof src[k] === "number" && !Number.isNaN(src[k])) return Math.max(0, src[k]);
    }
  }

  // 2) derive from an expiry-style date
  const dateKeys = ["expiryDate", "endDate", "validTill", "validUpto", "validUntil", "expiresAt", "expiry"];
  for (const src of sources) {
    for (const k of dateKeys) {
      if (src[k]) {
        const d = dayjs(src[k]);
        if (d.isValid()) return Math.max(0, d.startOf("day").diff(dayjs().startOf("day"), "day"));
      }
    }
  }

  return null;
};

/* ─── Component ──────────────────────────────────────────────────────── */

const AllLockers = () => {
  const [searchText, setSearchText] = useState("");
  const [activeTab,  setActiveTab]  = useState("all");
  const [page,       setPage]       = useState(1);
  const [limit,      setLimit]      = useState(12);

  const branchId = useSelector((state: any) => state.branch.selectedBranch);
  const selectedBranchId =
    typeof branchId === "object" ? (branchId?._id || branchId?.id) : branchId;

  // Reset to first page whenever the selected branch changes.
  useEffect(() => { setPage(1); }, [selectedBranchId]);

  const { data, isLoading } = useGetAllLockersQuery({
    search: searchText,
    status: activeTab !== "all" ? activeTab : undefined,
    branchId: selectedBranchId && selectedBranchId !== "all" ? selectedBranchId : undefined,
    page,
    limit,
  } as any);

  const apiData                    = data as any;
  const rawLockers: LockerRecord[] = apiData?.data       || [];
  const paginationInfo             = apiData?.pagination || {};
  const summary                    = apiData?.summary    || {};

  const statusTabs: StatusTab[] = useMemo(() => [
    { key: "all",       label: "All",       count: summary.all       ?? 0 },
    { key: "purchased", label: "Purchased", count: summary.purchased ?? 0 },
    { key: "available", label: "Available", count: summary.available ?? 0 },
  ], [summary.all, summary.purchased, summary.available]);

  // ── DEMO OVERLAY ───────────────────────────────────────────────
  // API abhi purchase/validity data nahi bhej raha, isliye demo data inject
  // kar rahe hain taaki UI (member + remaining days) dikh sake.
  // Backend ready hone par DEMO ko false kar dena / poora block hata dena.
  const lockers = useMemo<LockerRecord[]>(() => rawLockers.map((l, i) => {
    if (!DEMO || isPurchased(l)) return l;                 // real purchased data ko chhedo mat
    if (i % 3 === 0) return l;                             // har 3rd locker available rakho
    return {
      ...l,
      isPurchased:  true,
      memberName:   DEMO_MEMBERS[i % DEMO_MEMBERS.length],
      daysRemaining: DEMO_DAYS[i % DEMO_DAYS.length],      // 3–58 din ka spread, kuch "low"
    };
  }), [rawLockers]);

  const filteredData = useMemo<LockerRecord[]>(() => {
    let d = lockers;
    if (activeTab === "purchased") d = d.filter(l => isPurchased(l));
    else if (activeTab === "available") d = d.filter(l => !isPurchased(l));
    if (searchText) {
      const q = searchText.toLowerCase();
      d = d.filter(l => lockerNo(l).toLowerCase().includes(q) || userName(l).toLowerCase().includes(q));
    }
    return d;
  }, [lockers, activeTab, searchText]);

  const totalCount = useMemo(() => {
    if (activeTab !== "all" || searchText) return filteredData.length;
    return paginationInfo.total || filteredData.length;
  }, [filteredData, activeTab, searchText, paginationInfo.total]);

  return (
    <div className="all-lockers-page">
      <div className="header-section">
        <div className="left-col">
          <SearchBar
            value={searchText}
            onChange={setSearchText}
            placeholder="Search lockers by number or user..."
          />
          <StatusTabs
            activeTab={activeTab}
            onTabChange={(tab: string) => { setActiveTab(tab); setPage(1); }}
            tabs={statusTabs}
          />
        </div>
        <div className="right-col">
          <AddButton to={AddLockerRoute}>Add Locker</AddButton>
        </div>
      </div>

      {isLoading ? (
        <div className="lockers-loader"><Spin size="large" /></div>
      ) : filteredData.length === 0 ? (
        <Empty description="No lockers found" style={{ padding: "60px 0" }} />
      ) : (
        <div className="lockers-grid">
          {filteredData.map((locker, idx) => {
            const purchased = isPurchased(locker);
            const days      = daysLeft(locker);
            const lowDays   = days != null && days <= 7;
            const price     = formatPrice(locker);
            return (
              <div
                key={locker._id || idx}
                className={`locker-card ${purchased ? "purchased" : "available"}`}
              >
                <div className="locker-card-top">
                  <div className="locker-icon">
                    {purchased ? <LockOutlined /> : <UnlockOutlined />}
                  </div>
                  <span className={`locker-status-tag ${purchased ? "purchased" : "available"}`}>
                    <span className="dot" />
                    {purchased ? "Purchased" : "Available"}
                  </span>
                </div>

                <div className="locker-heading">
                  <h3 className="locker-number">{lockerNo(locker)}</h3>
                  {locker.branchId?.name && (
                    <p className="locker-branch">
                      <EnvironmentOutlined />
                      <span>{locker.branchId.name}</span>
                    </p>
                  )}
                  {price && <span className="locker-price">{price}</span>}
                </div>

                <div className="locker-divider" />

                {purchased ? (
                  <div className="locker-footer">
                    {memberId(locker) ? (
                      <Link to={`/user-detail/${memberId(locker)}/addon-service`} className="locker-user" style={{ color: 'inherit', textDecoration: 'none' }}>
                        <span className="avatar"><UserOutlined /></span>
                        <div className="user-meta">
                          <span className="user-name">{userName(locker)}</span>
                          <span className="user-sub">Member</span>
                        </div>
                      </Link>
                    ) : (
                      <div className="locker-user">
                        <span className="avatar"><UserOutlined /></span>
                        <div className="user-meta">
                          <span className="user-name">{userName(locker)}</span>
                          <span className="user-sub">Member</span>
                        </div>
                      </div>
                    )}
                    {days != null ? (
                      <span className={`days-badge ${lowDays ? "low" : ""}`}>
                        <ClockCircleOutlined />
                        {days} {days === 1 ? "day" : "days"}
                      </span>
                    ) : (
                      <span className="days-badge na">Validity N/A</span>
                    )}
                  </div>
                ) : (
                  <div className="locker-footer available-footer">
                    <span className="available-text">Open for purchase</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <CustomPagination
        current={page}
        pageSize={limit}
        total={totalCount}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />
    </div>
  );
};

export default AllLockers;
